/**
 * Hook: centralised material lifecycle management for imported 3D objects.
 *
 * Responsibilities:
 * 1. Replace non-standard materials (e.g. MeshPhongMaterial from AMFLoader)
 *    with MeshStandardMaterial (or MeshPhysicalMaterial) on first mount or
 *    when the object changes.
 * 2. Update existing materials IN-PLACE when scene settings change (metalness,
 *    roughness, physical properties, etc.) — without creating new material
 *    instances (unless switching between Standard/Physical).
 * 3. Track every material we create so we can dispose them on cleanup.
 * 4. When grayscaleMode is true, convert each material's color to its
 *    luminance-equivalent gray and force flat/non-reflective material
 *    properties. Original colors are recorded so they can be restored when
 *    grayscale is toggled off.
 */

import { useLayoutEffect, useRef } from 'react';
import * as THREE from 'three';
import type { MaterialSettings } from '@/contexts/SceneSettingsContext';
import { applySettingsToMaterial, disposeMaterial } from './materialUtils';

/** ITU-R BT.709 luma coefficients. */
function toLumaGray(color: THREE.Color): THREE.Color {
  const luma = 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b;
  return new THREE.Color(luma, luma, luma);
}

export function useMaterialManager(
  object: THREE.Object3D | undefined,
  materialSettings: MaterialSettings,
  grayscaleMode = false,
): void {
  // Set of materials we have created — we own their lifecycle.
  const ownedMaterials = useRef<Set<THREE.Material>>(new Set());
  // Track the last physical-mode so we know when to rebuild materials
  const lastPhysicalMode = useRef<boolean>(
    materialSettings.usePhysicalMaterial,
  );
  // Original colors recorded at Phase A — needed to restore when grayscale is toggled off.
  const originalColors = useRef<Map<THREE.MeshStandardMaterial, THREE.Color>>(
    new Map(),
  );

  // --- Phase A: replace non-standard materials or rebuild on physical mode change ---
  useLayoutEffect(() => {
    if (!object) return;

    const physicalModeChanged =
      lastPhysicalMode.current !== materialSettings.usePhysicalMaterial;
    lastPhysicalMode.current = materialSettings.usePhysicalMaterial;

    object.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      child.castShadow = true;
      child.receiveShadow = true;

      // Compute normals if missing
      if (child.geometry && !child.geometry.attributes.normal) {
        child.geometry.computeVertexNormals();
      }

      if (!child.material) return;
      const oldMat = child.material;

      // Skip array materials (complex multi-material meshes)
      if (Array.isArray(oldMat)) return;

      const needsReplacement =
        physicalModeChanged ||
        (!(oldMat instanceof THREE.MeshStandardMaterial) &&
          !(oldMat instanceof THREE.MeshPhysicalMaterial));

      if (needsReplacement) {
        const MatClass = materialSettings.usePhysicalMaterial
          ? THREE.MeshPhysicalMaterial
          : THREE.MeshStandardMaterial;

        // Record the original color before any grayscale override
        const originalColor = (oldMat as THREE.MeshStandardMaterial).color
          ? (oldMat as THREE.MeshStandardMaterial).color.clone()
          : new THREE.Color(1, 1, 1);

        const displayColor = grayscaleMode
          ? toLumaGray(originalColor)
          : originalColor;

        const newMatProps: THREE.MeshStandardMaterialParameters = {
          color: displayColor,
          vertexColors: oldMat.vertexColors,
          flatShading: materialSettings.flatShading,
          metalness: grayscaleMode ? 0 : materialSettings.metalness,
          roughness: grayscaleMode ? 1 : materialSettings.roughness,
          envMapIntensity: grayscaleMode ? 0 : materialSettings.envMapIntensity,
          side: THREE.DoubleSide,
        };

        const newMat = new MatClass(newMatProps);

        if (newMat instanceof THREE.MeshPhysicalMaterial && !grayscaleMode) {
          newMat.clearcoat = materialSettings.clearcoat;
          newMat.clearcoatRoughness = materialSettings.clearcoatRoughness;
          newMat.sheen = materialSettings.sheen;
          newMat.sheenRoughness = materialSettings.sheenRoughness;
          newMat.sheenColor = new THREE.Color(...materialSettings.sheenColor);
          newMat.iridescence = materialSettings.iridescence;
          newMat.iridescenceIOR = materialSettings.iridescenceIOR;
          newMat.transmission = materialSettings.transmission;
          newMat.ior = materialSettings.ior;
          newMat.thickness = materialSettings.thickness;
          newMat.attenuationColor = new THREE.Color(
            ...materialSettings.attenuationColor,
          );
          newMat.attenuationDistance = materialSettings.attenuationDistance;
        }

        child.material = newMat;
        ownedMaterials.current.add(newMat);
        // Record original color for grayscale restore
        originalColors.current.set(newMat, originalColor);

        // Dispose the old material
        if (ownedMaterials.current.has(oldMat)) {
          ownedMaterials.current.delete(oldMat);
          originalColors.current.delete(oldMat as THREE.MeshStandardMaterial);
        }
        disposeMaterial(oldMat);
      } else {
        // Already a standard/physical material — track it so we can update it later.
        // Record original color if not yet tracked.
        const stdMat = oldMat as THREE.MeshStandardMaterial;
        if (!originalColors.current.has(stdMat)) {
          originalColors.current.set(stdMat, stdMat.color.clone());
        }
        ownedMaterials.current.add(oldMat);
      }
    });

    // Cleanup: dispose all owned materials when object changes or unmounts
    return () => {
      ownedMaterials.current.forEach((mat) => disposeMaterial(mat));
      ownedMaterials.current.clear();
      originalColors.current.clear();
    };
  }, [object, materialSettings.usePhysicalMaterial]); // Re-run when object or physical mode changes

  // --- Phase B: update materials in-place when settings or grayscale mode change ---
  useLayoutEffect(() => {
    if (!object) return;

    object.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      if (!child.material) return;
      if (Array.isArray(child.material)) return;

      if (child.material instanceof THREE.MeshStandardMaterial) {
        const mat = child.material;

        if (grayscaleMode) {
          // Apply luma gray from the recorded original color
          const originalColor =
            originalColors.current.get(mat) ?? mat.color.clone();
          // Ensure we have it recorded for later restore
          if (!originalColors.current.has(mat)) {
            originalColors.current.set(mat, originalColor.clone());
          }
          mat.color = toLumaGray(originalColor);
          mat.roughness = 1.0;
          mat.metalness = 0.0;
          mat.envMapIntensity = 0;
          mat.flatShading = materialSettings.flatShading;
          mat.emissive = new THREE.Color(0, 0, 0);
          mat.emissiveIntensity = 0;
          mat.needsUpdate = true;
        } else {
          // Restore original color, then apply settings normally
          const originalColor = originalColors.current.get(mat);
          if (originalColor) {
            mat.color = originalColor.clone();
          }
          applySettingsToMaterial(mat, materialSettings);
        }
      }
    });
  }, [object, materialSettings, grayscaleMode]);
}
